import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PanelLayout from '@/components/PanelLayout';
import axios from 'axios';
import { API } from '@/utils/api';
import { Loader2, CheckCircle2, Circle, Rocket, ArrowRight, Trophy, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const STEP_ICONS = {
  profile: '👤',
  gallery: '📸',
  page: '🌐',
  campaign: '📣',
  leads: '💬',
};

const STEP_PATHS = {
  profile: '/panel/profil',
  gallery: '/panel/profil',
  page: '/panel/strona',
  campaign: '/panel/kampania',
  leads: '/panel/leady',
};

export default function AutoBoostGuide() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    axios.get(`${API}/autonomous/boost-guide`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  return (
    <PanelLayout>
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="boost-guide-page">
        <div className="text-center mb-2">
          <span className="inline-block px-3 py-1 rounded-full bg-[#F47A3B]/10 text-[#F47A3B] text-xs font-semibold mb-3">Zarabiaj więcej</span>
          <h1 className="text-3xl font-bold text-zinc-900 mb-2">Automatyczna promocja</h1>
          <p className="text-zinc-500 text-sm max-w-md mx-auto">Checklist, który maksymalizuje Twoje wyniki — uzupełnij wszystkie kroki, żeby system działał na pełnej mocy.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin" /></div>
        ) : (
          <div className="space-y-6">
            {/* Progress */}
            <div className="bg-white border border-zinc-100 rounded-2xl p-6" data-testid="boost-progress">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-[#F47A3B]/10 flex items-center justify-center">
                    {data?.percent >= 100 ? <Trophy className="w-6 h-6 text-[#F47A3B]" /> : <Rocket className="w-6 h-6 text-[#F47A3B]" />}
                  </div>
                  <div>
                    <p className="text-lg font-bold text-zinc-900">{data?.done || 0}/{data?.total || 5} kroków</p>
                    <p className="text-xs text-zinc-500">{data?.percent >= 100 ? 'System działa na pełnych obrotach!' : 'Wykonaj pozostałe kroki, żeby odblokować pełnię możliwości'}</p>
                  </div>
                </div>
                <div className="text-3xl font-bold" style={{ color: data?.percent >= 100 ? '#10B981' : '#F47A3B' }}>{data?.percent || 0}%</div>
              </div>
              {/* Progress bar */}
              <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${data?.percent || 0}%`, background: data?.percent >= 100 ? '#10B981' : '#F47A3B' }} />
              </div>
            </div>

            {/* Steps */}
            <div className="bg-white border border-zinc-100 rounded-2xl overflow-hidden" data-testid="boost-steps">
              {(data?.steps || []).map((step, i) => (
                <div
                  key={step.id}
                  className={`flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-zinc-50 transition-colors ${i > 0 ? 'border-t border-zinc-50' : ''}`}
                  onClick={() => !step.done && navigate(STEP_PATHS[step.id] || '/panel')}
                  data-testid={`boost-step-${step.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="text-xl">{STEP_ICONS[step.id] || '📋'}</div>
                    <div>
                      <p className={`text-sm font-medium ${step.done ? 'text-zinc-400 line-through' : 'text-zinc-900'}`}>{step.label}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">Aktualnie: {step.value}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {step.done ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <ArrowRight className="w-4 h-4 text-[#F47A3B]" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Quick stats */}
            {(data?.top_post || data?.leads_count > 0) && (
              <div className="grid grid-cols-2 gap-3">
                {data?.top_post && (
                  <div className="bg-white border border-zinc-100 rounded-2xl p-5 text-center">
                    <Zap className="w-5 h-5 text-amber-500 mx-auto mb-2" />
                    <p className="text-xl font-bold text-zinc-900">{data.top_post.reactions}</p>
                    <p className="text-xs text-zinc-500">reakcji (najlepszy post)</p>
                  </div>
                )}
                {data?.leads_count > 0 && (
                  <div className="bg-white border border-zinc-100 rounded-2xl p-5 text-center">
                    <Zap className="w-5 h-5 text-emerald-500 mx-auto mb-2" />
                    <p className="text-xl font-bold text-zinc-900">{data.leads_count}</p>
                    <p className="text-xs text-zinc-500">leadów zebranych</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </PanelLayout>
  );
}
